// File: src/layouts/BottomMenuBar.js
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useProfile } from "../context/ProfileContext";
import UserProfilePicture from '../components/profile/UserProfilePicture';

import flameIcon from "../assets/trends.png";
import broadcastIcon from "../assets/radio.png";
import podiumIcon from "../assets/podium.png";

// Dark Mode Icons (White)
import flameDark from "../assets/trends_darkmode.png";
import broadcastDark from "../assets/radio_darkmode.png";
import podiumDark from "../assets/podium_darkmode.png";

// Active Icons (Pink #FF00CC)
import flameActive from "../assets/trends_active.png";
import broadcastActive from "../assets/radio_active.png";
import podiumActive from "../assets/podium_active.png";

const BottomMenuBar = ({ onNavigate }) => {
  const location = useLocation();
  const { activeProfile, isDarkMode, isPremium } = useProfile();

  // Active route checking
  const isActive = (path) => {
    if (path === "/profile") {
      return location.pathname === "/profile" || location.pathname === "/profile/";
    }
    if (path === "/trending") {
      return location.pathname === "/trending" || location.pathname === "/trends";
    }
    return location.pathname === path;
  };

  const getIconSource = (path, standard, dark, active) => {
    if (isActive(path)) return active;
    if (isDarkMode) return dark;
    return standard;
  };

  // Uniform icon wrapper so all icons share the identical footprint
  const iconContainerStyle = "w-9 h-9 flex items-center justify-center";

  // Prevent layout shift: keep font-medium constant across active and inactive states
  const textStyle = (path) =>
    `text-xs mt-1 transition-colors duration-150 text-center font-medium select-none ${
      isActive(path) ? "text-[#ff00cc]" : "text-black dark:text-brand-white opacity-80"
    }`;

  const gradientOverlay = isDarkMode
    ? "linear-gradient(to top, rgba(0,0,0,0.95) 50%, rgba(0,0,0,0.7) 80%, transparent)"
    : "linear-gradient(to top, rgba(255,255,255,0.9) 50%, rgba(255,255,255,0.6) 80%, transparent)";

  const isProfileActive = isActive("/profile");

  return (
    <div className="relative w-full">
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{ background: gradientOverlay }}
      />

      {/* 🌟 Distributed evenly across the bottom: Removed 'max-w-md' restriction */}
      <div className="py-3 px-6 w-full flex items-center justify-around relative z-10 max-w-4xl mx-auto">
        
        {/* 1. Trends */}
        <Link 
          to="/trends" 
          onClick={onNavigate} 
          className="flex-1 max-w-[140px] flex flex-col items-center justify-center focus:outline-none"
        >
          <div className={iconContainerStyle}>
            <img 
              src={getIconSource("/trending", flameIcon, flameDark, flameActive)} 
              alt="Trends" 
              className="w-7 h-7 object-contain" 
            />
          </div>
          <span className={textStyle("/trending")}>Trends</span>
        </Link>

        {/* 2. Radio */}
        <Link 
          to="/radio" 
          onClick={onNavigate} 
          className="flex-1 max-w-[140px] flex flex-col items-center justify-center focus:outline-none"
        >
          <div className={iconContainerStyle}>
            <img 
              src={getIconSource("/radio", broadcastIcon, broadcastDark, broadcastActive)} 
              alt="Radio" 
              className="w-7 h-7 object-contain" 
            />
          </div>
          <span className={textStyle("/radio")}>Radio</span>
        </Link> 

        {/* 3. Charts */}
        <Link 
          to="/charts" 
          onClick={onNavigate} 
          className="flex-1 max-w-[140px] flex flex-col items-center justify-center focus:outline-none"
        >
          <div className={iconContainerStyle}>
            <img 
              src={getIconSource("/charts", podiumIcon, podiumDark, podiumActive)} 
              alt="Charts" 
              className="w-9 h-9 object-contain" 
            />
          </div>
          <span className={textStyle("/charts")}>Charts</span>
        </Link>

        {/* 4. Profile */}
        <Link 
          to="/profile" 
          onClick={onNavigate} 
          className="flex-1 max-w-[140px] flex flex-col items-center justify-center focus:outline-none"
        >
          <div className={iconContainerStyle}>
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 ${
                isProfileActive
                  ? "ring-1 ring-[#ff00cc] shadow-[0_0_8px_rgba(255,0,204,0.4)]"
                  : "ring-0 shadow-none"
              }`}
            >
              <UserProfilePicture 
                photoURL={activeProfile?.photoURL} 
                sizeClass="w-10 h-10 rounded-lg object-cover"
                shape="square"
                isPremium={activeProfile?.type === 'user' ? isPremium : false}
              />
            </div>
          </div>
          <span className={textStyle("/profile")}>
            Profile
          </span>
        </Link>

      </div>
    </div>
  );
};

export default BottomMenuBar;